import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { ROOMS } from '../rooms';
import { SLICE_DISTRICT } from '../config';
import { useWorldStore } from '../state/worldStore';
import { useCutscene, cutsceneBridge } from '../city/cutscene';
import { roomSpace } from '../state/interiorFloor';
import { useGreeting } from '../state/greeting';
import { takeAudio, releaseAudio, onAudio } from '../services/audioPool';
import { audioEngine } from '../services/audioEngine';

// THE GREETING AT THE DESK (2026-11-01). Khari: "let's add a cut scene when the
// user enters the HQ. Lyra will greet with a recorded message." Her lines are her
// own (rooms.js `greeting` ← data/hq_greetings.json), and so is the brief:
// *"keeping them short and unhurried. The world is the experience — I don't want
// to talk over it. The greeting should just let them know I'm real and I'm
// available, not perform for them."*
//
// So it is ONE shot: from where you landed, the camera eases toward her at the
// desk while she says one line in her recorded voice (her speaking face on,
// the station stepped aside, the line captioned), then eases back to exactly
// where you stood and the room is yours. Skip / Esc ends it at any moment (the
// cutscene bar's button). Never the same line twice running; not again within
// AGAIN_MS of the last (walking in and out is not a reason to hear her twice);
// never over an episode you are following; it waits for her body to be there.
//
// While it plays it holds the camera like every cutscene (cutsceneBridge): the
// room's walk, floor and furniture clamps, the follow camera and the doors stand
// down. Room space comes from MeshInterior's anchor (roomSpace), so a sited room
// turned with its building frames her the same way.
const AGAIN_MS = 10 * 60 * 1000;
const IN_S = 2.4;          // the ease toward her
const SPEAK_AT = 0.9;      // she starts while the camera is still settling
const TAIL_S = 0.7;        // a beat after the last word
const OUT_S = 1.3;         // the ease back to you
const WAIT_DESK_MS = 6000; // play anyway if her body never reports in
const EYE = 0.68;          // her eye line above the mark, for a 0.75 body
const HOLDER = 'room-greeting';

const mem = {
  get(k) { try { return localStorage.getItem(k); } catch { return null; } },
  set(k, val) { try { localStorage.setItem(k, val); } catch { /* private mode */ } },
};
const ease = (x) => { const t = Math.min(1, Math.max(0, x)); return t * t * (3 - 2 * t); };
// Fallback length when the file's own duration is unknown: an unhurried read.
const readSecs = (text) => 1 + String(text || '').split(/\s+/).length * 0.36;

function pickLine(roomId, lines) {
  const last = mem.get(`greet:${roomId}:last`);
  const pool = lines.length > 1 ? lines.filter((l) => String(l.id) !== last) : lines;
  const line = pool[Math.floor(Math.random() * pool.length)];
  mem.set(`greet:${roomId}:last`, String(line.id));
  return line;
}

export default function RoomGreeting({ roomId, k = 1 }) {
  const room = ROOMS[roomId];
  const g = room?.greeting;
  const desk = room?.frontDesk;
  const camera = useThree((s) => s.camera);
  const camSettled = useWorldStore((s) => s.camSettled);
  const charSelect = useWorldStore((s) => s.charSelect);
  const seated = useWorldStore((s) => s.seated);
  const npcList = useWorldStore((s) => s.npcList);
  const deskReady = useGreeting((s) => !!s.deskReady[roomId]);
  const run = useRef(null);
  const settledAt = useRef(0);
  const v = useMemo(() => ({
    face: new THREE.Vector3(), end: new THREE.Vector3(), look: new THREE.Vector3(),
    m: new THREE.Matrix4(), up: new THREE.Vector3(0, 1, 0),
  }), []);

  // The caption's name: the speaker's roster name up to its tag ("Lyra - …").
  const avatarId = desk?.who === 'assistant' ? SLICE_DISTRICT.assistantAvatarId : Number(desk?.avatarId);
  const speaker = useMemo(() => {
    const n = (npcList || []).find((x) => Number(x?.avatarId) === Number(avatarId))?.name || desk?.name || g?.speaker || '';
    return n.split(/\s+[-–—|]\s+/)[0];
  }, [npcList, avatarId, desk, g]);

  useEffect(() => { if (camSettled && !settledAt.current) settledAt.current = performance.now(); }, [camSettled]);

  const finish = (restore) => {
    const r = run.current;
    if (!r || r.done || !r.line) { if (r) r.done = true; return; }
    r.done = true;
    if (restore) { camera.position.copy(r.savedPos); camera.quaternion.copy(r.savedQuat); }
    cutsceneBridge.active = false; cutsceneBridge.at = null; cutsceneBridge.scene = null;
    if (useCutscene.getState().target?.kind === 'greet') useCutscene.setState({ target: null, phase: 'off', caption: null });
    useGreeting.getState().setTalking(null);
    if (r.el) { try { r.el.pause(); } catch { /* gone */ } releaseAudio(r.el); }
    audioEngine.roomQuiet(false, 900, HOLDER)?.catch?.(() => {});
  };
  // Left the room mid-greeting (Back to street): let go; the rig owns the camera.
  useEffect(() => () => finish(false), []); // eslint-disable-line react-hooks/exhaustive-deps

  // Esc skips, like the bar's button.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape' || !run.current || run.current.done || !run.current.line) return;
      if (useCutscene.getState().target?.kind === 'greet') useCutscene.getState().leave();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const start = () => {
    const line = pickLine(roomId, g.lines);
    mem.set(`greet:${roomId}:at`, String(Date.now()));
    const savedPos = camera.position.clone();
    const savedQuat = camera.quaternion.clone();
    const savedLook = savedPos.clone().addScaledVector(new THREE.Vector3(0, 0, -1).applyQuaternion(savedQuat), 2);
    cutsceneBridge.active = true; cutsceneBridge.at = savedPos.clone(); cutsceneBridge.scene = { kind: 'greet', id: roomId };
    useCutscene.setState({ target: { kind: 'greet', id: roomId, title: room.name }, phase: 'cut', caption: null });
    const el = takeAudio(`${g.audio.replace('{id}', line.id)}?v=${g.v}`);
    run.current = { t0: performance.now(), t: 0, stage: 'in', line, savedPos, savedQuat, savedLook, el, dur: null, spoke: null, done: false };
    onAudio(el, 'loadedmetadata', () => { const r = run.current; if (r && Number.isFinite(el.duration) && el.duration > 0) r.dur = el.duration; }, { once: true });
    audioEngine.roomQuiet(true, 600, HOLDER)?.catch?.(() => {});
    console.log(`[greeting] ${roomId}: line ${line.id}`);
  };

  const beginOut = (r) => {
    r.stage = 'out'; r.outT0 = performance.now();
    r.outPos = camera.position.clone(); r.outQuat = camera.quaternion.clone();
    useGreeting.getState().setTalking(null);
    if (useCutscene.getState().caption) useCutscene.getState().setCaption(null);
    if (r.el) { try { r.el.pause(); } catch { /* gone */ } }
  };

  useFrame(() => {
    if (!g?.lines?.length || !desk) return;
    let r = run.current;
    if (!r) {
      // WHEN: landed in the room, the body chosen, standing, her there (or long enough).
      if (!camSettled || charSelect || seated || !roomSpace.object || !settledAt.current || cutsceneBridge.active) return;
      const waited = performance.now() - settledAt.current;
      if (waited < 350 || (!deskReady && waited < WAIT_DESK_MS)) return;
      if (useCutscene.getState().target) { run.current = { done: true }; return; }   // following an episode
      if (Date.now() - (Number(mem.get(`greet:${roomId}:at`)) || 0) < AGAIN_MS) { run.current = { done: true }; return; }
      start();
      r = run.current;
    }
    if (r.done) return;
    const space = roomSpace.object;
    if (!space) { finish(false); return; }
    // ON THE WALL CLOCK, not frames: her voice plays in real time, and on a slow
    // device a frame-counted shot fell behind it (seen headless at ~1 fps).
    r.t = (performance.now() - r.t0) / 1000;

    // Skipped (the bar's button / Esc cleared the target): straight to the way back.
    const tgt = useCutscene.getState().target;
    if (r.stage !== 'out' && tgt?.kind !== 'greet') beginOut(r);

    // The shot, in room space: her eyes, and a spot in front of her and a little
    // to one side at her eye line — over the desk, never in it.
    const mx = desk.pos[0] * k; const my = (desk.pos[1] || 0) * k; const mz = desk.pos[2] * k;
    const rot = desk.rotY || 0; const fx = Math.sin(rot); const fz = Math.cos(rot);
    v.face.set(mx, my + EYE, mz); space.localToWorld(v.face);
    v.end.set(mx + fx * 0.82 + fz * 0.18, my + EYE + 0.03, mz + fz * 0.82 - fx * 0.18); space.localToWorld(v.end);

    if (r.stage === 'in' || r.stage === 'hold') {
      if (!r.spoke && r.t >= SPEAK_AT) {
        r.spoke = r.t;
        r.el?.play().catch(() => { /* the caption still reads */ });
        useGreeting.getState().setTalking(roomId);
        useCutscene.getState().setCaption({ name: speaker, text: r.line.text });
      }
      let at;
      if (r.stage === 'in') {
        const e = ease(r.t / IN_S);
        camera.position.lerpVectors(r.savedPos, v.end, e);
        v.look.lerpVectors(r.savedLook, v.face, e);
        if (r.t >= IN_S) r.stage = 'hold';
        at = v.look;
      } else {
        // An unhurried drift closer while she talks.
        const e = ease((r.t - IN_S) / 6);
        camera.position.lerpVectors(v.end, v.face, 0.08 * e);
        at = v.face;
      }
      v.m.lookAt(camera.position, at, v.up);
      camera.quaternion.setFromRotationMatrix(v.m);
      const ended = r.el?.ended || (r.spoke && r.t >= r.spoke + (r.dur ?? readSecs(r.line.text)) + TAIL_S);
      if (r.spoke && ended && r.t >= IN_S) beginOut(r);
      return;
    }

    // The way back: to exactly where you stood, looking where you looked.
    const e = ease((performance.now() - r.outT0) / 1000 / OUT_S);
    camera.position.lerpVectors(r.outPos, r.savedPos, e);
    camera.quaternion.slerpQuaternions(r.outQuat, r.savedQuat, e);
    if (e >= 1) finish(true);
  });

  return null;
}
